import { useEffect, useCallback, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import cn from "classnames";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { queryString, isValidLatitude, isValidLongitude, calculatePageCount } from "capitalrx-utilities";

import { AppleMap, MapCoordinates, ZipCodeSearchForm, ListItem, PanelContainer, PharmacyMapCallout, MapLocation, MapBounds } from "@/basic/components";
import { SkeletonList, Pagination } from "@/common/components";
import { DEFAULT_MAP_COORDINATES } from "@/common/constants/mapConstants";
import { useCheckCurrentScreen } from "@/basic/hooks";

import styles from "./styles.module.scss";

import { useListPharmacy } from "@/api-hooks";
import { PharmacyInfo, ListPharmacyReturn } from "@/api/types";

export interface PharmacyListContainerProps {
  handleRedoSearch: (coordinates: MapBounds) => void;
  handleItemClick: (providerId: string) => void;
  client: string;
  coverageType?: string;
  zipCodeFormPushTo: string;
  panelTitle?: ReactNode;
}

export const PharmacyListContainer = ({
  zipCodeFormPushTo,
  handleRedoSearch,
  handleItemClick,
  client,
  coverageType,
  panelTitle,
}: PharmacyListContainerProps) => {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const getCurrentQueryStrings = queryString.convertQueryStringToJson(location.search);
  const pharmacyName = getCurrentQueryStrings?.pharmacy_name?.toString();
  const RESULTS_PER_PAGE = 25;
  const showAlert = useAlert();

  const failureHandler = useCallback(() => {
    showAlert({
      type: "error",
      style: "toast",
      message: translate("PHARMACY_LIST_CONTAINER.canNotFindPage"),
    });
  }, [showAlert]);
  const [coordinates, setCoordinates] = useState<MapCoordinates>({ latitude: null, longitude: null });
  const [pharmacyData, setPharmacyData] = useState<ListPharmacyReturn["results"]>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const { data: pharmacyList, isError, isLoading } = useListPharmacy(
    {
      client: client ? client : undefined,
      coverage_type: coverageType ? coverageType : undefined,
      pharmacy_name: pharmacyName,

      // @ts-ignore
      latitude: getCurrentQueryStrings?.lat?.toString(),

      // @ts-ignore
      longitude: getCurrentQueryStrings?.lng?.toString(),
      results_per_page: RESULTS_PER_PAGE,
      page_number: currentPage + 1,
    },
    {
      enabled: !!client && !!coverageType && !!getCurrentQueryStrings?.lat && !!getCurrentQueryStrings?.lng,
    },
  );

  useEffect(() => {
    async function fetchData() {
      // @ts-ignore
      setPharmacyData(pharmacyList?.results);
      // @ts-ignore
      setPageCount(calculatePageCount(parseInt(pharmacyList?.["result_count"]), RESULTS_PER_PAGE));

      setCoordinates({
        latitude: Number(getCurrentQueryStrings?.lat?.toString()),
        longitude: Number(getCurrentQueryStrings?.lng?.toString()),
      });
    }

    fetchData();

  }, [location, currentPage, pharmacyList]);

  if (isError) {
    failureHandler();
  }

  const screen = useCheckCurrentScreen();
  const wideScreen = screen === "desktop" || screen === "tablet";

  const listContentListClass = cn(styles["ListContentList"], { [styles["ListContentMobileList"]]: !wideScreen });
  const itemListWrapperClass = cn(styles["ItemListWrapper"], { ItemListMobileWrapperList: !wideScreen });
  const ListPageGoogleMapWrapperClass = cn(
    { [styles["ListPageGoogleMapWrapper"]]: wideScreen },
    { [styles["ListPageMobileGoogleMapWrapper"]]: !wideScreen },
  );
  const center = {
    latitude: DEFAULT_MAP_COORDINATES.lat,
    longitude: DEFAULT_MAP_COORDINATES.lng,
  };

  if (isValidLatitude(coordinates.latitude) && isValidLongitude(coordinates.longitude)) {
    center.latitude = coordinates.latitude;
    center.longitude = coordinates.longitude;
  }

  const handleSelectPage = (pageNum: number) => {
    setCurrentPage(pageNum);
  };

  const map = (
    <AppleMap
      className={ListPageGoogleMapWrapperClass}
      style={{ height: "1px", maxHeight: "400px" }}
      renderCalloutContent={(location) => (
        <PharmacyMapCallout
          distance={location.data?.distance}
          id={location.data.provider_id}
          data={location.data as PharmacyInfo} />
      )}
      center={center}
      locations={pharmacyData?.map((item): MapLocation => ({
        latitude: parseFloat(item.pharmacy.latitude),
        longitude: parseFloat(item.pharmacy.longitude),
        title: item.pharmacy.name,
        data: item.pharmacy,
      }))}
      onSearchArea={handleRedoSearch}
    />
  );

  let pharmacyContent = isLoading ? <SkeletonList /> : <></>;
  if (!isLoading && pharmacyData) {
    pharmacyContent = pharmacyData?.length ? (
      <Stack className={itemListWrapperClass} style={{ paddingRight: "0.5em" }}>
        {pharmacyData.map((data: { pharmacy: PharmacyInfo }) => {
          return (
            <div key={data.pharmacy.provider_id}>
              <ListItem
                name={data.pharmacy.name}
                distance={`${data.pharmacy.distance} ${translate("PHARMACY_LIST_CONTAINER.miles")}`}
                address1={data.pharmacy.address_line_1}
                address2={data.pharmacy.address_line_2}
                city={data.pharmacy.city}
                state={data.pharmacy.state}
                zip_code={data.pharmacy.zip}
                providerId={data.pharmacy.provider_id}
                handleItemClick={handleItemClick}
              />
            </div>
          );
        })}
        <Pagination selectedPage={currentPage} pageCount={pageCount} handleSelectPage={handleSelectPage} />
      </Stack>
    ) : (
      <Stack>
        <span>{`${translate("PHARMACY_LIST_CONTAINER.zeroResultsFound")} ${pharmacyName}.`}</span>
        <span>
          <em>{translate("PHARMACY_LIST_CONTAINER.tryCheckingYourSpelling")}</em>
        </span>
      </Stack>
    );
  }

  return (
    <PanelContainer>
      <Stack>
        {panelTitle}
        <div className={styles["ListContentWrapper"]}>
          <Stack className={listContentListClass}>
            <ZipCodeSearchForm pushTo={zipCodeFormPushTo} />

            {!wideScreen && pharmacyData && map}

            {pharmacyContent}
          </Stack>

          {wideScreen && map}
        </div>
      </Stack>
    </PanelContainer>
  );
};
